import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ThemeProvider, createTheme, CssBaseline, AppBar, Toolbar,
  Box, Typography, CircularProgress, TextField, Switch,
  Chip, IconButton, Tooltip, Collapse, Divider, Button,
  Tabs, Tab, Select, MenuItem,
} from "@mui/material";
import axios from "axios";
import { API } from "../api/api";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary:    { main: "#6c7ef8" },
    background: { default: "#0d0e14", paper: "#13151e" },
    text:       { primary: "#f0f2f7", secondary: "#b0b7ce" },
  },
  typography: { fontFamily: "'DM Sans', system-ui, sans-serif" },
});

const D = {
  bg:           "#0d0e14",
  surface:      "#13151e",
  surfaceUp:    "#191c28",
  surfaceHover: "#1e2135",
  border:       "rgba(255,255,255,0.07)",
  borderMed:    "rgba(255,255,255,0.11)",
  accent:       "#6c7ef8",
  accentSoft:   "rgba(108,126,248,0.10)",
  accentBorder: "rgba(108,126,248,0.25)",
  purple:       "#a37bf8",
  purpleSoft:   "rgba(163,123,248,0.10)",
  success:      "#34d399",
  successSoft:  "rgba(52,211,153,0.10)",
  successBorder:"rgba(52,211,153,0.25)",
  warn:         "#f59e0b",
  warnSoft:     "rgba(245,158,11,0.10)",
  danger:       "#f87171",
  dangerSoft:   "rgba(248,113,113,0.10)",
  teal:         "#2dd4bf",
  tealSoft:     "rgba(45,212,191,0.10)",
  text:         "#f0f2f7",
  textSec:      "#b0b7ce",
  textMuted:    "#6b7390",
  font:         "'DM Sans',sans-serif",
  fontDisplay:  "'Plus Jakarta Sans','DM Sans',sans-serif",
};

function adminAxios() {
  const token = localStorage.getItem("admin_token");
  return axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });
}

function statusColor(status) {
  if (status === "active")   return { color: D.success, bg: D.successSoft, border: D.successBorder };
  if (status === "canceling") return { color: D.warn, bg: D.warnSoft, border: "rgba(245,158,11,0.25)" };
  if (status === "past_due") return { color: D.danger, bg: D.dangerSoft, border: "rgba(248,113,113,0.25)" };
  return { color: D.textMuted, bg: "rgba(107,115,144,0.08)", border: "rgba(107,115,144,0.18)" };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FlagSwitch({ checked, onChange, loading }) {
  return (
    <Switch
      checked={checked}
      onChange={onChange}
      disabled={loading}
      size="small"
      sx={{
        "& .MuiSwitch-switchBase.Mui-checked": { color: D.accent },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { background: D.accent },
        "& .MuiSwitch-track": { background: "rgba(255,255,255,0.12)" },
      }}
    />
  );
}

function ClientRow({ client, onFlagChange }) {
  const [expanded,  setExpanded]  = useState(false);
  const [subData,   setSubData]   = useState(null);
  const [subLoading,setSubLoading]= useState(false);
  const [toggling,  setToggling]  = useState({});
  const [resetting, setResetting] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [copied,    setCopied]    = useState(false);
  const [creditsOpen,  setCreditsOpen]  = useState(false);
  const [creditAmts,   setCreditAmts]   = useState({ video: 1, ebook: 1, "email-sequence": 1 });
  const [addingCredit, setAddingCredit] = useState({});
  const [localCredits, setLocalCredits] = useState({
    video:             client.video_credits || 0,
    ebook:             client.ebook_credits || 0,
    "email-sequence":  client.email_sequence_credits || 0,
  });
  const [quotaAmts,    setQuotaAmts]    = useState({ "content-gen": 1, "trend-search": 1 });
  const [adjustingQuota, setAdjustingQuota] = useState({});
  const [quotaData,    setQuotaData]    = useState(null);

  const sc = statusColor(client.stripe_subscription_status);

  async function loadSub() {
    if (subData) { setExpanded(e => !e); return; }
    setExpanded(true);
    setSubLoading(true);
    try {
      const res = await adminAxios().get(`/admin/clients/${encodeURIComponent(client.email)}/subscription`);
      setSubData(res.data);
    } catch {
      setSubData({ error: true });
    } finally {
      setSubLoading(false);
    }
  }

  async function toggleFlag(flag, value) {
    setToggling(t => ({ ...t, [flag]: true }));
    try {
      await adminAxios().patch(`/admin/clients/${encodeURIComponent(client.email)}/flags`, { [flag]: value });
      onFlagChange(client.email, flag, value);
    } catch {
      /* silently ignore — toggle reverts visually because state wasn't updated */
    } finally {
      setToggling(t => ({ ...t, [flag]: false }));
    }
  }

  async function handleReset() {
    setResetting(true);
    setResetLink("");
    try {
      const res = await adminAxios().post(`/admin/clients/${encodeURIComponent(client.email)}/reset-password`);
      setResetLink(res.data.reset_link);
    } catch {
      setResetLink("error");
    } finally {
      setResetting(false);
    }
  }

  function copyLink() {
    navigator.clipboard.writeText(resetLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function addCredits(type) {
    setAddingCredit(a => ({ ...a, [type]: true }));
    try {
      const res = await adminAxios().post(
        `/admin/clients/${encodeURIComponent(client.email)}/${type}-credits`,
        { amount: creditAmts[type] }
      );
      const key = type === "video" ? "video_credits" : type === "ebook" ? "ebook_credits" : "email_sequence_credits";
      setLocalCredits(lc => ({ ...lc, [type]: res.data[key] }));
    } catch { /* silent */ }
    setAddingCredit(a => ({ ...a, [type]: false }));
  }

  async function fetchQuota() {
    try {
      const res = await adminAxios().get(`/admin/clients/${encodeURIComponent(client.email)}/usage`);
      setQuotaData(res.data);
    } catch { /* silent */ }
  }

  async function adjustQuota(type, sign) {
    setAdjustingQuota(a => ({ ...a, [type]: true }));
    try {
      const endpoint = type === "content-gen" ? "content-generation-quota" : "trend-search-quota";
      const res = await adminAxios().post(
        `/admin/clients/${encodeURIComponent(client.email)}/${endpoint}`,
        { amount: sign * quotaAmts[type] }
      );
      setQuotaData(prev => {
        if (!prev) return prev;
        const limits = { ...(prev.limits || {}) };
        const usage  = { ...(prev.usage  || {}) };
        if (type === "content-gen") {
          limits.content_generations = res.data.content_generations_limit;
          usage.content_generations  = res.data.content_generations_used;
        } else {
          limits.trend_searches = res.data.trend_searches_limit;
          usage.trend_searches  = res.data.trend_searches_used;
        }
        return { ...prev, limits, usage };
      });
    } catch { /* silent */ }
    setAdjustingQuota(a => ({ ...a, [type]: false }));
  }

  function handleCreditsOpen() {
    const next = !creditsOpen;
    setCreditsOpen(next);
    if (next && !quotaData) fetchQuota();
  }

  return (
    <Box sx={{
      border: `1px solid ${D.border}`,
      borderRadius: "16px",
      background: D.surface,
      overflow: "hidden",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": { borderColor: D.borderMed, boxShadow: "0 4px 24px rgba(0,0,0,0.3)" },
    }}>
      {/* Main row */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr auto auto auto auto auto auto auto auto auto" },
        alignItems: "center",
        gap: { xs: 1.5, md: 2 },
        px: { xs: 2, md: 3 },
        py: 2.25,
      }}>
        {/* Email + name */}
        <Box>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: D.text, fontFamily: D.font }}>
            {client.email}
          </Typography>
          {client.name && (
            <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font, mt: 0.25 }}>
              {client.name}
            </Typography>
          )}
          {client.created_at && (
            <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, mt: 0.25 }}>
              Joined {new Date(client.created_at).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        {/* Subscription status */}
        <Box>
          <Chip
            label={client.stripe_subscription_status || "none"}
            size="small"
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              fontFamily: D.font,
              color: sc.color,
              background: sc.bg,
              border: `1px solid ${sc.border}`,
              borderRadius: "6px",
              height: "22px",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          />
        </Box>

        {/* Auto-Posting toggle */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Auto Post
          </Typography>
          <FlagSwitch
            checked={client.auto_posting}
            onChange={e => toggleFlag("auto_posting", e.target.checked)}
            loading={!!toggling.auto_posting}
          />
        </Box>

        {/* Freestyle Run toggle */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Freestyle Run
          </Typography>
          <FlagSwitch
            checked={client.has_campaign_mode}
            onChange={e => toggleFlag("has_campaign_mode", e.target.checked)}
            loading={!!toggling.has_campaign_mode}
          />
        </Box>

        {/* Compliance toggle */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Compliance
          </Typography>
          <FlagSwitch
            checked={client.has_compliance}
            onChange={e => toggleFlag("has_compliance", e.target.checked)}
            loading={!!toggling.has_compliance}
          />
        </Box>

        {/* Content Intelligence toggle */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Intel Suite
          </Typography>
          <FlagSwitch
            checked={client.has_content_intelligence}
            onChange={e => toggleFlag("has_content_intelligence", e.target.checked)}
            loading={!!toggling.has_content_intelligence}
          />
        </Box>

        {/* Medium Articles toggle */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Medium
          </Typography>
          <FlagSwitch
            checked={client.has_medium}
            onChange={e => toggleFlag("has_medium", e.target.checked)}
            loading={!!toggling.has_medium}
          />
        </Box>

        {/* eBook toggle */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            eBook
          </Typography>
          <FlagSwitch
            checked={client.has_ebook}
            onChange={e => toggleFlag("has_ebook", e.target.checked)}
            loading={!!toggling.has_ebook}
          />
        </Box>

        {/* Email Sequences toggle */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Email Seq
          </Typography>
          <FlagSwitch
            checked={client.has_email_sequences}
            onChange={e => toggleFlag("has_email_sequences", e.target.checked)}
            loading={!!toggling.has_email_sequences}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Video
          </Typography>
          <FlagSwitch
            checked={client.has_video}
            onChange={e => toggleFlag("has_video", e.target.checked)}
            loading={!!toggling.has_video}
          />
        </Box>

        {/* Manage credits */}
        <Tooltip title="Manage credits" placement="top">
          <IconButton
            onClick={handleCreditsOpen}
            size="small"
            sx={{
              color: creditsOpen ? D.accent : D.textMuted,
              "&:hover": { color: D.accent, background: D.accentSoft },
              borderRadius: "8px",
              width: 32, height: 32,
            }}
          >
            <span style={{ fontSize: "14px" }}>💳</span>
          </IconButton>
        </Tooltip>

        {/* Reset password */}
        <Tooltip title="Send password reset" placement="top">
          <IconButton
            onClick={handleReset}
            disabled={resetting}
            size="small"
            sx={{
              color: D.textMuted,
              "&:hover": { color: D.warn, background: D.warnSoft },
              borderRadius: "8px",
              width: 32, height: 32,
            }}
          >
            {resetting ? <CircularProgress size={14} sx={{ color: D.warn }} /> : <span style={{ fontSize: "15px" }}>🔑</span>}
          </IconButton>
        </Tooltip>

        {/* Expand subscription */}
        <Tooltip title={expanded ? "Hide billing" : "View billing"} placement="top">
          <IconButton
            onClick={loadSub}
            size="small"
            sx={{
              color: D.textMuted,
              "&:hover": { color: D.accent, background: D.accentSoft },
              borderRadius: "8px",
              width: 32, height: 32,
              transform: expanded ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
            }}
          >
            <span style={{ fontSize: "13px" }}>▾</span>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Reset link display */}
      {resetLink && (
        <Box sx={{ px: 3, pb: 1.5 }}>
          {resetLink === "error" ? (
            <Typography sx={{ fontSize: "12px", color: D.danger }}>Failed to generate reset link.</Typography>
          ) : (
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1,
              background: D.warnSoft, border: `1px solid rgba(245,158,11,0.25)`,
              borderRadius: "8px", px: 1.5, py: 1,
            }}>
              <Typography sx={{ fontSize: "11px", color: D.warn, fontFamily: "'JetBrains Mono',monospace", flex: 1, wordBreak: "break-all" }}>
                {resetLink}
              </Typography>
              <Button
                onClick={copyLink}
                size="small"
                sx={{ fontSize: "11px", color: D.warn, textTransform: "none", minWidth: 0, px: 1, py: 0.25, fontFamily: D.font }}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Credits panel */}
      <Collapse in={creditsOpen}>
        <Divider sx={{ borderColor: D.border }} />
        <Box sx={{ px: 3, py: 2 }}>
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Credits
          </Typography>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
            {[{ type: "video", label: "Video" }, { type: "ebook", label: "eBook" }, { type: "email-sequence", label: "Email Seq" }].map(({ type, label }) => (
              <Box key={type} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>
                  {label}: <strong style={{ color: D.text }}>{localCredits[type]}</strong>
                </Typography>
                <Box
                  component="input"
                  type="number"
                  min="1"
                  value={creditAmts[type]}
                  onChange={e => setCreditAmts(a => ({ ...a, [type]: Math.max(1, parseInt(e.target.value) || 1) }))}
                  style={{
                    width: 52, background: "#16161f", border: "1px solid #1e1e2e", borderRadius: "6px",
                    padding: "4px 8px", fontSize: "12px", color: "#e8e8f0", fontFamily: "Inter,sans-serif",
                    outline: "none", textAlign: "center",
                  }}
                />
                <Button
                  size="small"
                  onClick={() => addCredits(type)}
                  disabled={!!addingCredit[type]}
                  sx={{
                    textTransform: "none", fontSize: "11px", color: D.accent,
                    border: `1px solid ${D.accentBorder}`, borderRadius: "6px", px: 1.5, py: 0.4,
                    "&:hover": { background: D.accentSoft },
                    "&:disabled": { opacity: 0.5 },
                  }}
                >
                  {addingCredit[type] ? "…" : "+ Add"}
                </Button>
              </Box>
            ))}
          </Box>
          <Divider sx={{ borderColor: D.border, mb: 2 }} />
          <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
            Monthly Quota
          </Typography>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {[
              { type: "content-gen",  label: "Content Gen",   limitKey: "content_generations", usedKey: "content_generations" },
              { type: "trend-search", label: "Trend Search",  limitKey: "trend_searches",       usedKey: "trend_searches" },
            ].map(({ type, label, limitKey, usedKey }) => {
              const limit = quotaData?.limits?.[limitKey] ?? "—";
              const used  = quotaData?.usage?.[usedKey]   ?? "—";
              return (
                <Box key={type} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>
                    {label}: <strong style={{ color: D.text }}>{typeof limit === "number" ? `${used}/${limit}` : "—"}</strong>
                  </Typography>
                  <Box
                    component="input"
                    type="number"
                    min="1"
                    value={quotaAmts[type]}
                    onChange={e => setQuotaAmts(a => ({ ...a, [type]: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{
                      width: 48, background: "#16161f", border: "1px solid #1e1e2e", borderRadius: "6px",
                      padding: "4px 8px", fontSize: "12px", color: "#e8e8f0", fontFamily: "Inter,sans-serif",
                      outline: "none", textAlign: "center",
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => adjustQuota(type, 1)}
                    disabled={!!adjustingQuota[type]}
                    sx={{
                      textTransform: "none", fontSize: "11px", color: D.accent,
                      border: `1px solid ${D.accentBorder}`, borderRadius: "6px", px: 1.5, py: 0.4, minWidth: 0,
                      "&:hover": { background: D.accentSoft },
                      "&:disabled": { opacity: 0.5 },
                    }}
                  >
                    {adjustingQuota[type] ? "…" : "+ Add"}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => adjustQuota(type, -1)}
                    disabled={!!adjustingQuota[type]}
                    sx={{
                      textTransform: "none", fontSize: "11px", color: D.danger,
                      border: `1px solid ${D.danger}44`, borderRadius: "6px", px: 1.5, py: 0.4, minWidth: 0,
                      "&:hover": { background: `${D.danger}12` },
                      "&:disabled": { opacity: 0.5 },
                    }}
                  >
                    {adjustingQuota[type] ? "…" : "− Reduce"}
                  </Button>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Collapse>

      {/* Expanded subscription info */}
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: D.border }} />
        <Box sx={{ px: 3, py: 2 }}>
          {subLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} sx={{ color: D.accent }} />
              <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>Loading billing…</Typography>
            </Box>
          ) : subData?.error ? (
            <Typography sx={{ fontSize: "12px", color: D.danger, fontFamily: D.font }}>Failed to load billing data.</Typography>
          ) : subData?.status === "no_subscription" ? (
            <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>No active subscription.</Typography>
          ) : subData ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {[
                { label: "Plan",     value: subData.plan },
                { label: "Status",   value: subData.status },
                { label: "Amount",   value: `${subData.currency} ${subData.amount}/${subData.interval}` },
                { label: "Renews",   value: subData.cancel_at_period_end ? "Cancels at period end" : new Date(subData.current_period_end).toLocaleDateString() },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
                  <Typography sx={{ fontSize: "13px", color: D.text, fontFamily: D.font, fontWeight: 500, mt: 0.25 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}


// ── Audit Log ─────────────────────────────────────────────────────────────────

const LOG_STATUS_COLORS = {
  complete: { color: "#34d399", bg: "rgba(52,211,153,0.10)", border: "rgba(52,211,153,0.25)" },
  ready:    { color: "#34d399", bg: "rgba(52,211,153,0.10)", border: "rgba(52,211,153,0.25)" },
  running:  { color: "#6c7ef8", bg: "rgba(108,126,248,0.10)", border: "rgba(108,126,248,0.25)" },
  processing:{ color: "#6c7ef8", bg: "rgba(108,126,248,0.10)", border: "rgba(108,126,248,0.25)" },
  queued:   { color: "#f59e0b", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.25)" },
  failed:   { color: "#f87171", bg: "rgba(248,113,113,0.10)", border: "rgba(248,113,113,0.25)" },
  canceled: { color: "#6b7390", bg: "rgba(107,115,144,0.08)", border: "rgba(107,115,144,0.18)" },
};

const FEATURE_META = {
  pipeline:       { label: "Pipeline",    color: "#6c7ef8", emoji: "⚡" },
  ebook:          { label: "eBook",       color: "#a37bf8", emoji: "📖" },
  email_sequence: { label: "Email Seq",   color: "#2dd4bf", emoji: "✉️" },
  video:          { label: "Video",       color: "#f59e0b", emoji: "🎬" },
};

function featureChip(feature) {
  const m = FEATURE_META[feature] || { label: feature, color: "#6b7390", emoji: "•" };
  return (
    <Chip
      label={`${m.emoji} ${m.label}`}
      size="small"
      sx={{
        fontSize: "10px", fontWeight: 600, fontFamily: D.font,
        color: m.color,
        background: `${m.color}18`,
        border: `1px solid ${m.color}40`,
        borderRadius: "6px", height: "20px",
      }}
    />
  );
}

function logStatusChip(status) {
  const c = LOG_STATUS_COLORS[status] || LOG_STATUS_COLORS.canceled;
  return (
    <Chip
      label={status || "unknown"}
      size="small"
      sx={{
        fontSize: "10px", fontWeight: 700, fontFamily: D.font,
        color: c.color, background: c.bg, border: `1px solid ${c.border}`,
        borderRadius: "6px", height: "20px", textTransform: "uppercase", letterSpacing: "0.04em",
      }}
    />
  );
}

function formatDuration(created, updated) {
  if (!created || !updated) return "—";
  const ms = new Date(updated) - new Date(created);
  if (ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function LogRow({ log }) {
  const [expanded,  setExpanded]  = useState(false);
  const [timeline,  setTimeline]  = useState(null);
  const [tlLoading, setTlLoading] = useState(false);

  const fetchTimeline = async () => {
    if (timeline !== null) { setExpanded(e => !e); return; }
    setExpanded(true);
    setTlLoading(true);
    try {
      const res = await adminAxios().get(`/admin/pipeline-logs/${log.task_id}/timeline`);
      setTimeline(res.data.steps || []);
    } catch {
      setTimeline([]);
    } finally {
      setTlLoading(false);
    }
  };

  const isFailed = log.status === "failed";
  const rowBorder = isFailed ? "rgba(248,113,113,0.25)" : (expanded ? D.borderMed : D.border);

  return (
    <Box sx={{ borderRadius: "10px", border: `1px solid ${rowBorder}`, overflow: "hidden", background: D.surface }}>
      {/* Main row */}
      <Box
        onClick={fetchTimeline}
        sx={{
          display: "grid",
          gridTemplateColumns: "150px 1fr 110px 100px 130px 70px 24px",
          gap: 1.5, px: 2, py: 1.5,
          alignItems: "center", cursor: "pointer",
          "&:hover": { background: D.surfaceHover },
          background: isFailed ? "rgba(248,113,113,0.04)" : "transparent",
        }}
      >
        <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font }}>
          {log.created_at ? new Date(log.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
        </Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "12px", color: D.text, fontFamily: D.font, fontWeight: 500 }}>
            {log.email || "—"}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, mt: 0.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {log.topic || "—"}
          </Typography>
        </Box>
        <Box>{featureChip(log.feature)}</Box>
        <Box>{logStatusChip(log.status)}</Box>
        <Box sx={{ minWidth: 0 }}>
          {isFailed && log.error ? (
            <Typography sx={{ fontSize: "10px", color: D.danger, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {log.error.slice(0, 35)}{log.error.length > 35 ? "…" : ""}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font }}>
              {log.step ? `${log.step}${log.pct ? ` (${log.pct}%)` : ""}` : "—"}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font }}>
          {formatDuration(log.created_at, log.updated_at)}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: D.textMuted, lineHeight: 1, userSelect: "none" }}>
          {expanded ? "▲" : "▼"}
        </Typography>
      </Box>

      {/* Expandable timeline */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: `1px solid ${D.border}`, background: "rgba(0,0,0,0.2)" }}>
          {tlLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
              <CircularProgress size={12} sx={{ color: D.accent }} />
              <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>Loading step history…</Typography>
            </Box>
          ) : !timeline || timeline.length === 0 ? (
            <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font, py: 1 }}>
              No step history yet (only new runs are logged at this level).
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
              <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                Step Timeline · Job {log.task_id?.slice(0, 8)}…
              </Typography>
              {timeline.map((step, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0.4 }}>
                    <Box sx={{
                      width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                      bgcolor: step.status === "complete" ? D.success
                             : step.status === "failed"   ? D.danger
                             : step.status === "canceled" ? D.textMuted
                             : D.accent,
                    }} />
                    {i < timeline.length - 1 && (
                      <Box sx={{ width: "1px", flex: 1, minHeight: 10, bgcolor: D.border, mt: 0.25 }} />
                    )}
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: step.status === "failed" ? D.danger : D.text, fontFamily: D.font }}>
                        {step.step}
                      </Typography>
                      {step.pct > 0 && step.status === "running" && (
                        <Typography sx={{ fontSize: "10px", color: D.accent, fontFamily: "'JetBrains Mono',monospace" }}>
                          {step.pct}%
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font }}>
                        {step.created_at ? new Date(step.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : ""}
                      </Typography>
                    </Box>
                    {step.label && step.label !== step.step && (
                      <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, mt: 0.1 }}>
                        {step.label}
                      </Typography>
                    )}
                    {step.error && (
                      <Box sx={{ mt: 0.5, p: 1, borderRadius: "6px", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)" }}>
                        <Typography sx={{ fontSize: "11px", color: D.danger, fontFamily: "'JetBrains Mono',monospace", wordBreak: "break-all" }}>
                          {step.error}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function AuditLog() {
  const [logs,         setLogs]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [emailFilter,   setEmailFilter]   = useState("");
  const [topicFilter,   setTopicFilter]   = useState("");
  const [statusFilter,  setStatusFilter]  = useState("");
  const [featureFilter, setFeatureFilter] = useState("");
  const [daysFilter,    setDaysFilter]    = useState(30);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (emailFilter.trim())   params.set("email",   emailFilter.trim());
      if (topicFilter.trim())   params.set("topic",   topicFilter.trim());
      if (statusFilter)         params.set("status",  statusFilter);
      if (featureFilter)        params.set("feature", featureFilter);
      params.set("days",  String(daysFilter));
      params.set("limit", "300");
      const res = await adminAxios().get(`/admin/pipeline-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
    } catch {
      setError("Failed to load pipeline logs.");
    } finally {
      setLoading(false);
    }
  }, [emailFilter, topicFilter, statusFilter, featureFilter, daysFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const failedCount = logs.filter(l => l.status === "failed").length;
  const completeCount = logs.filter(l => l.status === "complete").length;

  return (
    <Box>
      {/* Stats bar */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Total", value: logs.length, color: D.textSec },
          { label: "Complete", value: completeCount, color: D.success },
          { label: "Failed", value: failedCount, color: failedCount > 0 ? D.danger : D.textMuted },
        ].map(s => (
          <Box key={s.label} sx={{ px: 2, py: 1, borderRadius: "8px", background: D.surface, border: `1px solid ${D.border}`, minWidth: 80, textAlign: "center" }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: s.color, fontFamily: D.font }}>{s.value}</Typography>
            <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          placeholder="Client email…"
          value={emailFilter}
          onChange={e => setEmailFilter(e.target.value)}
          size="small"
          sx={{
            flex: "1 1 180px",
            "& .MuiOutlinedInput-root": { borderRadius: "10px", background: D.surface, fontFamily: D.font, fontSize: "13px" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: D.border },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: D.accent, borderWidth: "1.5px" },
            "& input": { color: D.text, fontFamily: D.font },
            "& input::placeholder": { color: D.textMuted, opacity: 1 },
          }}
        />
        <TextField
          placeholder="Topic keyword…"
          value={topicFilter}
          onChange={e => setTopicFilter(e.target.value)}
          size="small"
          sx={{
            flex: "1 1 160px",
            "& .MuiOutlinedInput-root": { borderRadius: "10px", background: D.surface, fontFamily: D.font, fontSize: "13px" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: D.border },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: D.accent, borderWidth: "1.5px" },
            "& input": { color: D.text, fontFamily: D.font },
            "& input::placeholder": { color: D.textMuted, opacity: 1 },
          }}
        />
        <Select
          value={featureFilter}
          onChange={e => setFeatureFilter(e.target.value)}
          displayEmpty
          size="small"
          sx={{
            minWidth: 130, borderRadius: "10px", background: D.surface, fontFamily: D.font,
            fontSize: "13px", color: featureFilter ? D.text : D.textMuted,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: D.border },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: D.accent },
            "& .MuiSvgIcon-root": { color: D.textMuted },
          }}
        >
          <MenuItem value="" sx={{ fontFamily: D.font, fontSize: "13px", color: D.textMuted }}>All features</MenuItem>
          {[
            { v: "pipeline",       l: "⚡ Pipeline" },
            { v: "ebook",          l: "📖 eBook" },
            { v: "email_sequence", l: "✉️ Email Seq" },
            { v: "video",          l: "🎬 Video" },
          ].map(o => (
            <MenuItem key={o.v} value={o.v} sx={{ fontFamily: D.font, fontSize: "13px" }}>{o.l}</MenuItem>
          ))}
        </Select>
        <Select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          displayEmpty
          size="small"
          sx={{
            minWidth: 120, borderRadius: "10px", background: D.surface, fontFamily: D.font,
            fontSize: "13px", color: statusFilter ? D.text : D.textMuted,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: D.border },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: D.accent },
            "& .MuiSvgIcon-root": { color: D.textMuted },
          }}
        >
          <MenuItem value="" sx={{ fontFamily: D.font, fontSize: "13px", color: D.textMuted }}>All statuses</MenuItem>
          {["complete", "ready", "running", "queued", "failed", "canceled"].map(s => (
            <MenuItem key={s} value={s} sx={{ fontFamily: D.font, fontSize: "13px", textTransform: "capitalize" }}>{s}</MenuItem>
          ))}
        </Select>
        <Select
          value={daysFilter}
          onChange={e => setDaysFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: 120, borderRadius: "10px", background: D.surface, fontFamily: D.font,
            fontSize: "13px", color: D.text,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: D.border },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: D.accent },
            "& .MuiSvgIcon-root": { color: D.textMuted },
          }}
        >
          {[7, 14, 30, 60].map(d => (
            <MenuItem key={d} value={d} sx={{ fontFamily: D.font, fontSize: "13px" }}>Last {d} days</MenuItem>
          ))}
        </Select>
        <Button
          onClick={fetchLogs}
          disabled={loading}
          size="small"
          sx={{ color: D.accent, fontFamily: D.font, textTransform: "none", fontSize: "13px", border: `1px solid ${D.accentBorder}`, borderRadius: "8px", px: 2, "&:hover": { background: D.accentSoft } }}
        >
          {loading ? <CircularProgress size={13} sx={{ color: D.accent }} /> : "↻ Refresh"}
        </Button>
      </Box>

      {error && (
        <Typography sx={{ color: D.danger, fontFamily: D.font, mb: 2, fontSize: "13px" }}>{error}</Typography>
      )}

      {/* Column headers */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "150px 1fr 110px 100px 130px 70px 24px",
        gap: 1.5, px: 2, mb: 1,
      }}>
        {["Timestamp", "Email / Topic", "Feature", "Status", "Last Step / Error", "Duration", ""].map(h => (
          <Typography key={h} sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {h}
          </Typography>
        ))}
      </Box>

      {/* Log rows */}
      {loading && logs.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: D.accent }} />
        </Box>
      ) : logs.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ color: D.textMuted, fontFamily: D.font }}>No pipeline jobs found.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {logs.map(log => <LogRow key={log.task_id} log={log} />)}
        </Box>
      )}
    </Box>
  );
}


// ── Create Test User Panel ────────────────────────────────────────────────────

const ALL_FLAGS = [
  { key: "auto_posting",             label: "Auto Post" },
  { key: "has_campaign_mode",        label: "Freestyle Run" },
  { key: "has_compliance",           label: "Compliance" },
  { key: "has_content_intelligence", label: "Intel Suite" },
  { key: "has_medium",               label: "Medium" },
  { key: "has_ebook",                label: "eBook" },
  { key: "has_email_sequences",      label: "Email Seq" },
  { key: "has_video",                label: "Video" },
];

function CreateTestUserPanel({ onCreated }) {
  const [open,       setOpen]       = useState(false);
  const [email,      setEmail]      = useState("");
  const [name,       setName]       = useState("");
  const [genLimit,   setGenLimit]   = useState(4);
  const [flags,      setFlags]      = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState("");

  const toggleFlag = (key) =>
    setFlags(f => f.includes(key) ? f.filter(k => k !== key) : [...f, key]);

  const reset = () => {
    setEmail(""); setName(""); setGenLimit(4); setFlags([]);
    setResult(null); setError(""); setOpen(false);
  };

  const submit = async () => {
    if (!email.trim()) { setError("Email is required."); return; }
    setError("");
    setSubmitting(true);
    try {
      const res = await adminAxios().post("/admin/test-users", {
        email: email.trim().toLowerCase(),
        name:  name.trim(),
        content_generations: genLimit,
        trend_searches: 10,
        flags,
      });
      setResult(res.data);
      onCreated?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create user.");
    }
    setSubmitting(false);
  };

  const inputStyle = {
    background: "#16161f", border: "1px solid #1e1e2e", borderRadius: "8px",
    padding: "8px 12px", fontSize: "13px", color: "#e8e8f0",
    fontFamily: "Inter,sans-serif", outline: "none", width: "100%", boxSizing: "border-box",
  };

  return (
    <Box sx={{ mb: 3 }}>
      {!open ? (
        <Button
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            textTransform: "none", fontSize: "12px", color: D.teal,
            border: "1px solid rgba(45,212,191,0.25)", borderRadius: "8px", px: 2, py: 0.75,
            "&:hover": { background: "rgba(45,212,191,0.08)" },
          }}
        >
          ➕ Create Test User
        </Button>
      ) : (
        <Box sx={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: "12px", p: 3 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: D.text, fontFamily: D.font, mb: 2 }}>
            Create Test User
          </Typography>

          {result ? (
            <Box>
              <Box sx={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: "8px", p: 2, mb: 2 }}>
                <Typography sx={{ fontSize: "12px", color: D.success, fontFamily: D.font, fontWeight: 600, mb: 0.5 }}>
                  User created: {result.email}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>
                  Temp password: <code style={{ color: D.text, background: "#16161f", padding: "2px 6px", borderRadius: "4px" }}>{result.temp_password}</code>
                </Typography>
              </Box>
              <Button onClick={reset} size="small" sx={{ textTransform: "none", fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>
                Close
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 2 }}>
                  <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, mb: 0.5 }}>Email *</Typography>
                  <input style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} placeholder="test@example.com" />
                </Box>
                <Box sx={{ flex: 2 }}>
                  <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, mb: 0.5 }}>Name</Typography>
                  <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="Test User" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, mb: 0.5 }}>Content runs</Typography>
                  <input style={{ ...inputStyle, textAlign: "center" }} type="number" min="1" value={genLimit} onChange={e => setGenLimit(Math.max(1, parseInt(e.target.value) || 1))} />
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, mb: 1 }}>Flags</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {ALL_FLAGS.map(({ key, label }) => (
                    <Box
                      key={key}
                      onClick={() => toggleFlag(key)}
                      sx={{
                        px: 1.5, py: 0.5, borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontFamily: D.font, fontWeight: 600,
                        border: flags.includes(key) ? `1px solid ${D.accent}` : `1px solid ${D.border}`,
                        color: flags.includes(key) ? D.accent : D.textMuted,
                        background: flags.includes(key) ? D.accentSoft : "transparent",
                        userSelect: "none",
                        transition: "all 0.15s",
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>

              {error && <Typography sx={{ fontSize: "12px", color: D.danger, fontFamily: D.font }}>{error}</Typography>}

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  onClick={submit}
                  disabled={submitting}
                  size="small"
                  sx={{
                    textTransform: "none", fontSize: "12px", fontWeight: 600, color: "#fff",
                    background: D.accent, borderRadius: "8px", px: 2,
                    "&:hover": { background: "#6a58e8" },
                    "&:disabled": { opacity: 0.5 },
                  }}
                >
                  {submitting ? "Creating…" : "Create"}
                </Button>
                <Button onClick={reset} size="small" sx={{ textTransform: "none", fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}


// ── Video Requests Panel ──────────────────────────────────────────────────────

const VIDEO_STATUS_META = {
  queued:      { label: "Queued",      color: "#6b6b8a" },
  processing:  { label: "Generating",  color: "#7c6aff" },
  ready:       { label: "Ready",       color: "#f59e0b" },
  in_progress: { label: "In Progress", color: "#7c6aff" },
  delivered:   { label: "Delivered",   color: "#22c55e" },
  failed:      { label: "Failed",      color: "#ef4444" },
};

function VideoRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState("");
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAxios().get("/admin/video-requests?limit=200");
      setRequests(res.data.requests || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateRequest = async (jobId, patch) => {
    setUpdating(p => ({ ...p, [jobId]: true }));
    try {
      await adminAxios().patch(`/admin/video-requests/${jobId}`, patch);
      setRequests(prev => prev.map(r => r.job_id === jobId ? { ...r, ...patch } : r));
    } catch { /* silent */ }
    setUpdating(p => ({ ...p, [jobId]: false }));
  };

  const getPackage = async (jobId) => {
    if (expanded === jobId) { setExpanded(null); return; }
    try {
      const res = await adminAxios().get(`/admin/video-requests/${jobId}/package`);
      setExpanded(jobId);
      setRequests(prev => prev.map(r => r.job_id === jobId ? { ...r, _package: res.data } : r));
    } catch { /* silent */ }
  };

  const filtered = requests.filter(r =>
    !filter || r.email?.includes(filter) || r.status?.includes(filter) || r.format?.includes(filter)
  );

  const pendingCount = requests.filter(r => r.status === "ready").length;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center", flexWrap: "wrap" }}>
        <Typography sx={{ fontFamily: D.font, fontWeight: 700, fontSize: "15px", color: D.text }}>
          Video Requests
          {pendingCount > 0 && (
            <Box component="span" sx={{ ml: 1, px: 1, py: 0.25, background: "rgba(245,158,11,0.15)", color: "#f59e0b",
              border: "1px solid rgba(245,158,11,0.3)", borderRadius: "5px", fontSize: "11px", fontWeight: 700 }}>
              {pendingCount} ready
            </Box>
          )}
        </Typography>
        <Box component="input"
          placeholder="Filter by email / status / format…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ flex: 1, minWidth: 200, background: "#16161f", border: "1px solid #1e1e2e", borderRadius: "8px",
            padding: "7px 12px", fontSize: "13px", color: "#e8e8f0", fontFamily: "Inter,sans-serif", outline: "none" }}
        />
        <Button onClick={load} size="small" sx={{ textTransform: "none", color: D.textMuted, fontSize: "12px" }}>Refresh</Button>
      </Box>

      {loading && <Box sx={{ textAlign: "center", py: 5 }}><CircularProgress size={24} sx={{ color: D.accent }} /></Box>}

      {!loading && filtered.length === 0 && (
        <Typography sx={{ fontSize: "13px", color: D.textMuted, textAlign: "center", py: 4 }}>No video requests found.</Typography>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {filtered.map(req => {
          const meta    = VIDEO_STATUS_META[req.status] || VIDEO_STATUS_META.queued;
          const isOpen  = expanded === req.job_id;
          const pkg     = req._package;

          return (
            <Box key={req.job_id} sx={{ border: `1px solid ${D.border}`, borderRadius: "12px", background: D.surface, overflow: "hidden" }}>
              {/* Row */}
              <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: "1fr auto", gap: 2, alignItems: "start" }}>
                <Box>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.75 }}>
                    <Typography sx={{ fontSize: "12px", fontFamily: "'JetBrains Mono',monospace", color: D.textMuted }}>{req.email}</Typography>
                    <Box component="span" sx={{ px: 0.75, py: 0.1, borderRadius: "4px", fontSize: "10px", fontWeight: 700,
                      background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}30` }}>
                      {meta.label}
                    </Box>
                    <Box component="span" sx={{ px: 0.75, py: 0.1, borderRadius: "4px", fontSize: "10px",
                      background: "rgba(255,255,255,0.04)", color: D.textMuted }}>
                      {req.format?.toUpperCase()} · {req.duration}s · {req.style}
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "13px", color: D.textSec, fontStyle: "italic", mb: 0.5 }}>
                    "{(req.post_content || "").slice(0, 100)}{(req.post_content || "").length > 100 ? "…" : ""}"
                  </Typography>
                  {req.notes && <Typography sx={{ fontSize: "11.5px", color: D.textMuted }}>Notes: {req.notes}</Typography>}

                  <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                    <Button size="small" onClick={() => getPackage(req.job_id)}
                      sx={{ textTransform: "none", fontSize: "11px", color: D.accent, border: `1px solid ${D.accentBorder}`, borderRadius: "6px",
                        "&:hover": { background: D.accentSoft } }}>
                      {isOpen ? "▲ Hide Package" : "▼ View Full Package"}
                    </Button>
                    {req.status === "ready" && (
                      <Button size="small" onClick={() => updateRequest(req.job_id, { status: "in_progress" })}
                        disabled={!!updating[req.job_id]}
                        sx={{ textTransform: "none", fontSize: "11px", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "6px",
                          "&:hover": { background: "rgba(245,158,11,0.08)" } }}>
                        Mark In Progress
                      </Button>
                    )}
                    {(req.status === "in_progress" || req.status === "ready") && (
                      <Button size="small" onClick={() => {
                        const url = window.prompt("Enter video URL to deliver:");
                        if (url) updateRequest(req.job_id, { status: "delivered", video_url: url });
                      }}
                        disabled={!!updating[req.job_id]}
                        sx={{ textTransform: "none", fontSize: "11px", color: "#22c55e", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "6px",
                          "&:hover": { background: "rgba(34,197,94,0.08)" } }}>
                        Mark Delivered
                      </Button>
                    )}
                  </Box>
                </Box>

                <Typography sx={{ fontSize: "10.5px", color: D.textMuted, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap", mt: 0.5 }}>
                  {req.created_at ? new Date(req.created_at).toLocaleDateString() : ""}
                </Typography>
              </Box>

              {/* Expanded package view */}
              {isOpen && pkg && (
                <Box sx={{ borderTop: `1px solid ${D.border}`, p: 2.5, background: "#0d0d14" }}>
                  {[
                    ["Client",         pkg.email],
                    ["Format",         `${pkg.format?.toUpperCase()} · ${pkg.duration}s · ${pkg.style}`],
                    ["Source Post",    pkg.post_content],
                    ["Client Notes",   pkg.notes || "None"],
                  ].map(([label, val]) => val && (
                    <Box key={label} sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "9px", color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.25 }}>{label}</Typography>
                      <Typography sx={{ fontSize: "12.5px", color: D.textSec }}>{val}</Typography>
                    </Box>
                  ))}

                  {pkg.script && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "9px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1 }}>Script</Typography>
                      <Box sx={{ p: 2, background: "#0a0a0f", borderRadius: "8px", border: `1px solid ${D.border}`, maxHeight: 320, overflow: "auto" }}>
                        <Typography sx={{ fontSize: "12px", color: "#c0c7de", whiteSpace: "pre-wrap", fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.7 }}>{pkg.script}</Typography>
                      </Box>
                    </Box>
                  )}

                  {pkg.transcript && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "9px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>Voiceover Transcript</Typography>
                      <Box sx={{ p: 1.5, background: "#0a0a0f", borderRadius: "8px", border: `1px solid ${D.border}` }}>
                        <Typography sx={{ fontSize: "12px", color: "#c0c7de", whiteSpace: "pre-wrap" }}>{pkg.transcript}</Typography>
                      </Box>
                    </Box>
                  )}

                  {pkg.music && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "9px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>Music Direction</Typography>
                      <Box sx={{ p: 1.5, background: "#0a0a0f", borderRadius: "8px", border: `1px solid ${D.border}` }}>
                        <Typography sx={{ fontSize: "12px", color: "#c0c7de", whiteSpace: "pre-wrap" }}>{pkg.music}</Typography>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${D.border}`, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "11px", color: D.textMuted }}>Add note for client:</Typography>
                    <Box component="input"
                      defaultValue={req.admin_notes || ""}
                      onBlur={e => { if (e.target.value !== req.admin_notes) updateRequest(req.job_id, { admin_notes: e.target.value }); }}
                      placeholder="e.g. Your video is ready — check your email"
                      style={{ flex: 1, minWidth: 200, background: "#16161f", border: "1px solid #2a2a3e",
                        borderRadius: "6px", padding: "5px 10px", fontSize: "12px", color: "#e8e8f0",
                        fontFamily: "Inter,sans-serif", outline: "none" }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}


// ── Main AdminPanel ───────────────────────────────────────────────────────────

export default function AdminPanel() {
  const navigate  = useNavigate();
  const [tab,      setTab]      = useState(0);
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [error,    setError]    = useState("");

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminAxios().get("/admin/clients");
      setClients(res.data.clients || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login", { replace: true });
      } else {
        setError("Failed to load clients.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  function handleFlagChange(email, flag, value) {
    setClients(prev => prev.map(c => c.email === email ? { ...c, [flag]: value } : c));
  }

  function handleLogout() {
    localStorage.removeItem("admin_token");
    navigate("/admin/login", { replace: true });
  }

  const filtered = clients.filter(c =>
    !search || c.email.toLowerCase().includes(search.toLowerCase()) || (c.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = clients.filter(c => c.stripe_subscription_status === "active").length;

  const tabSx = {
    fontFamily: D.font,
    fontSize: "13px",
    textTransform: "none",
    fontWeight: 600,
    color: D.textMuted,
    minHeight: "40px",
    "&.Mui-selected": { color: D.accent },
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        body { background: #0d0e14; -webkit-font-smoothing: antialiased; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes orbFloat1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(30px,-18px)} }
        @keyframes orbFloat2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-20px,24px)} }
        .ap-fade { animation: fadeUp 0.4s cubic-bezier(0.22,1,0.36,1) both; }
        .ap-orb1 { position:fixed;width:640px;height:640px;border-radius:50%;z-index:0;pointer-events:none;background:radial-gradient(circle,rgba(108,126,248,0.05) 0%,transparent 70%);top:-220px;left:-220px;animation:orbFloat1 18s ease-in-out infinite; }
        .ap-orb2 { position:fixed;width:520px;height:520px;border-radius:50%;z-index:0;pointer-events:none;background:radial-gradient(circle,rgba(45,212,191,0.04) 0%,transparent 70%);bottom:-160px;right:-140px;animation:orbFloat2 22s ease-in-out infinite; }
      `}</style>
      <div className="ap-orb1" />
      <div className="ap-orb2" />

      <Box sx={{ minHeight: "100vh", background: D.bg, position: "relative", zIndex: 1 }}>

        {/* Top nav */}
        <AppBar position="sticky" elevation={0} sx={{
          background: "rgba(13,14,20,0.88)",
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${D.border}`,
          zIndex: 10,
        }}>
          <Toolbar sx={{ gap: 2, minHeight: "60px !important", px: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: `linear-gradient(135deg, ${D.accent} 0%, ${D.purple} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 14, color: "#fff", boxShadow: "0 4px 14px rgba(108,126,248,0.35)", animation: "logoGlow 3s ease infinite" }}>Z</Box>
              <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", color: D.text, letterSpacing: "-0.02em" }}>
                zenithpost<span style={{ color: D.accent }}>.ai</span>
              </Typography>
            </Box>
            <Box sx={{ width: "1px", height: 18, background: D.border }} />
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: "99px", px: 1.5, py: 0.4 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: D.danger }} />
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: D.danger, fontFamily: "'JetBrains Mono',monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Admin Console
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={tab === 0 ? fetchClients : undefined}
              disabled={loading}
              size="small"
              sx={{ color: D.textMuted, fontFamily: D.font, textTransform: "none", fontSize: "13px", border: `1px solid ${D.border}`, borderRadius: "8px", px: 1.5, "&:hover": { color: D.text, borderColor: D.borderMed } }}
            >
              {loading ? <CircularProgress size={13} sx={{ color: D.textMuted, mr: 0.75 }} /> : "↻"} Refresh
            </Button>
            <Button
              onClick={handleLogout}
              size="small"
              sx={{ color: D.danger, fontFamily: D.font, textTransform: "none", fontSize: "13px", border: "1px solid rgba(248,113,113,0.2)", borderRadius: "8px", px: 1.5, "&:hover": { background: D.dangerSoft } }}
            >
              Sign out
            </Button>
          </Toolbar>
        </AppBar>

        <Box sx={{ maxWidth: 1160, mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              mb: 4,
              borderBottom: `1px solid ${D.border}`,
              "& .MuiTabs-indicator": { background: `linear-gradient(90deg, ${D.accent}, ${D.purple})`, height: "2px", borderRadius: "2px" },
              minHeight: "44px",
            }}
          >
            <Tab label="Clients" sx={tabSx} />
            <Tab label="Pipeline Audit Log" sx={tabSx} />
            <Tab label="Video Requests" sx={tabSx} />
          </Tabs>

          {/* ── Clients tab ── */}
          {tab === 0 && (
            <>
              {/* Stats row */}
              <Box className="ap-fade" sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(110px,1fr))", gap: 1.5, mb: 4 }}>
                {[
                  { label: "Total Clients",  value: clients.length,     color: D.accent,  icon: "👥" },
                  { label: "Active",         value: activeCount,         color: D.success, icon: "✓" },
                  { label: "Freestyle Run",  value: clients.filter(c => c.has_campaign_mode).length,        color: D.teal,   icon: "⚡" },
                  { label: "Compliance",     value: clients.filter(c => c.has_compliance).length,           color: D.purple, icon: "🛡️" },
                  { label: "Intel Suite",    value: clients.filter(c => c.has_content_intelligence).length, color: D.accent, icon: "🧠" },
                  { label: "Medium",         value: clients.filter(c => c.has_medium).length,               color: D.teal,   icon: "📝" },
                  { label: "eBook",          value: clients.filter(c => c.has_ebook).length,                color: D.purple, icon: "📘" },
                  { label: "Email Seq",      value: clients.filter(c => c.has_email_sequences).length,      color: D.warn,   icon: "✉️" },
                  { label: "Video",          value: clients.filter(c => c.has_video).length,                color: D.accent, icon: "🎬" },
                ].map(({ label, value, color, icon }) => (
                  <Box key={label} sx={{
                    background: D.surface,
                    border: `1px solid ${D.border}`,
                    borderRadius: "14px",
                    px: 2, py: 1.75,
                    position: "relative", overflow: "hidden",
                    transition: "border-color 0.2s, transform 0.2s",
                    "&:hover": { borderColor: D.borderMed, transform: "translateY(-1px)" },
                  }}>
                    <Box sx={{ position: "absolute", top: 0, left: "20%", right: "20%", height: "1px", background: `linear-gradient(90deg, transparent, ${color}44, transparent)` }} />
                    <Typography sx={{ fontSize: "17px", mb: 0.5 }}>{icon}</Typography>
                    <Typography sx={{ fontSize: "28px", fontFamily: D.fontDisplay, fontWeight: 800, color, letterSpacing: "-0.04em", lineHeight: 1 }}>
                      {value}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", mt: 0.5 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Search */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  placeholder="Search by email or name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px", background: D.surface, fontFamily: D.font, fontSize: "14px" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: D.border },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: D.borderMed },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: D.accent, borderWidth: "1.5px" },
                    "& input": { color: D.text, fontFamily: D.font },
                    "& input::placeholder": { color: D.textMuted, opacity: 1 },
                  }}
                />
              </Box>

              {/* Create test user */}
              <CreateTestUserPanel onCreated={fetchClients} />

              {/* Column labels */}
              <Box sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns: "1fr auto auto auto auto auto auto auto auto auto auto",
                gap: 2, px: 3, mb: 1,
              }}>
                {["Client", "Status", "Auto Post", "Freestyle Run", "Compliance", "Intel Suite", "Medium", "eBook", "Video", "", "", ""].map((label, i) => (
                  <Typography key={i} sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.font, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: i > 0 ? "center" : "left" }}>
                    {label}
                  </Typography>
                ))}
              </Box>

              {/* Client list */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress sx={{ color: D.accent }} />
                </Box>
              ) : error ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography sx={{ color: D.danger, fontFamily: D.font, mb: 2 }}>{error}</Typography>
                  <Button onClick={fetchClients} sx={{ color: D.accent, fontFamily: D.font, textTransform: "none" }}>Try again</Button>
                </Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography sx={{ color: D.textMuted, fontFamily: D.font }}>
                    {search ? "No clients match your search." : "No clients yet."}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {filtered.map(client => (
                    <ClientRow
                      key={client.email}
                      client={client}
                      onFlagChange={handleFlagChange}
                    />
                  ))}
                </Box>
              )}
            </>
          )}

          {/* ── Audit Log tab ── */}
          {tab === 1 && <AuditLog />}

          {/* ── Video Requests tab ── */}
          {tab === 2 && <VideoRequestsPanel />}

        </Box>
      </Box>
    </ThemeProvider>
  );
}
